import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { getApi } from "../../../Services/apiService";
import {
  getMemberBirthCalendarParts,
  getMemberDobTimestamp,
} from "../../../Utils/family-tree/dateAndTime";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  formatDashboardMemberDisplayName,
  getDashboardMemberNameParts,
  isDashboardDeceasedMember,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardDeceasedAngel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardLineColorName from "./DashboardLineColorName";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";
import {
  buildBirthByDayGroups,
  fetchBirthByDay,
} from "../../../Utils/dashboardBirthByDay";
import {
  DASHBOARD_MEMBER_FILTERS,
  applyDashboardMemberFilterToggle,
  filterDashboardMembersByMemberFilter,
  getDashboardBaseSortForMemberFilter,
} from "../../../Utils/dashboardDetailMemberFilter";
import { sortDashboardMembersByDeathDate } from "../../../Utils/dashboardFamilyLineNumbers";
import DashboardBackButton from "./DashboardBackButton";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";

// The report lists its members by first name, A–Z, within each day.
const DEFAULT_SORT = "alpha-asc";

const MONTH_SHORT_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** The birth month, short ("Jan"), shown between the name and the line. */
const getBirthMonthShortName = (member) => {
  const parts = getMemberBirthCalendarParts(member);
  return parts ? MONTH_SHORT_NAMES[parts.month] ?? "" : "";
};

/** The birth month as 0–11, or -1 when the record has no usable date. */
const getBirthMonthIndex = (member) => {
  const parts = getMemberBirthCalendarParts(member);
  return parts && Number.isInteger(parts.month) ? parts.month : -1;
};

/**
 * Birth Day (1-31, Calendar) — rpt_ByBirthDay, laid out as Milestone Ages this
 * Year is: the day of the month in the badge on the left, the family member
 * and their line across the middle, the day's total on the right.
 *
 * Lists exactly what the endpoint returns (see buildBirthByDayGroups):
 * descendants and in-laws, living and deceased. The member tabs narrow it to
 * Descendants (Living / Deceased) or In-Laws (Living / Deceased), as on
 * Birthdays this Month.
 */
function BirthByDay() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  const [rosterMembers, setRosterMembers] = useState([]);
  const [reportLoading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(true);
  // The report's own days, 1–31, minus the empty ones.
  const [reportDays, setReportDays] = useState([]);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT); // "none", "month", "alpha-asc", "alpha-desc", "color", "oldest", "youngest"
  // "all" is the opening view: descendants plus the living in-laws.
  const [memberFilter, setMemberFilter] = useState(DASHBOARD_MEMBER_FILTERS.ALL);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLargeView] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  // The phone's footer key comes from the roster, a separate and slower call. A
  // report opened ahead of it showed a short legend that grew moments later, so
  // the phone holds the page for it; a failed roster call still lets it open.
  const loading = reportLoading || (isCompactLayout && rosterLoading);
  const listRef = useRef(null);

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) {
          setReportDays([]);
          return;
        }

        const report = await fetchBirthByDay(family_tree_id);
        if (!cancelled) setReportDays(report.days);
      } catch (error) {
        console.error("Error fetching birth by day:", error);
        if (!cancelled) setReportDays([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();
    return () => {
      cancelled = true;
    };
  }, []);

  // The roster supplies what the report rows leave out — photos, multiple
  // births — and the head-of-line map the line link goes through.
  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode === 200 && response?.data) {
          const members = extractAllMembersFromStatisticsResponse(response.data);
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              members
            )
          );
          setRosterMembers(members);
        }
      } catch (error) {
        console.error("Error fetching roster for birth by day:", error);
      } finally {
        setRosterLoading(false);
      }
    };

    fetchRoster();
  }, []);

  // Each day's rows exactly as birth-by-day lists them, filled out from the
  // roster (photos, multiple births) but never added to from it.
  const groupedDays = useMemo(
    () => buildBirthByDayGroups(reportDays, rosterMembers),
    [reportDays, rosterMembers]
  );

  // Flat list of everyone in the report — what the column measuring below
  // sizes the name and head-of-line columns against.
  const allMembers = useMemo(
    () => groupedDays.flatMap((group) => group.details),
    [groupedDays]
  );

  // The phone's footer key, as on Family Members. Built from the whole tree,
  // not this page's rows, so it lists the same family lines as every other
  // detail page.
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(rosterMembers, headsMap),
    [rosterMembers, headsMap]
  );

  // The Start/Top of Line couple's records carry their middle names in the
  // first-name field ("Joseph Alexander", "Frances Virginia"); the rows show
  // the first name alone, like every other member on the list.
  const withoutStartTopMiddleName = (member) => {
    if (!isExplicitDashboardStartOrTopOfLineMember(member)) return member;
    const firstName = String(member?.first_name ?? member?.firstName ?? "").trim();
    return { ...member, first_name: firstName.split(/\s+/)[0], firstName: undefined };
  };

  // The rows leave out the "(descendant)" parenthetical after an in-law's
  // name; the in-law "*" still marks them.
  const formatPersonName = (member) =>
    formatDashboardMemberDisplayName(withoutStartTopMiddleName(member), {
      appendDescendantSpouse: false,
    });

  const renderMemberRow = (member) => {
    const { baseName } = getDashboardMemberNameParts(withoutStartTopMiddleName(member), {
      appendDescendantSpouse: false,
    });
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);
    const showInLawAsterisk =
      isDashboardInLawMember(member) && !isExplicitDashboardStartOrTopOfLineMember(member);
    const headOfLineName = getDashboardFamilyLineHeadDisplayName(member, headsMap);
    const hexCode = member?.family_line_hex_code || member?.hex_code || "#000000";
    const colorName = member?.family_line_color || member?.color_name || "";

    return (
      <div className={`ages-milestone-member-row ${isLargeView ? 'large-view' : ''}`}>
        {/* Column 1: Person Name */}
        <div className={`ages-milestone-member-name ${isLargeView ? 'large-view' : ''}`} style={{ minWidth: "var(--birth-by-day-name-col, 0px)" }}>
          <DashboardNameMarkerGroup>
          <DashboardPhotoName
            {...getDashboardMemberNamePhotoUrls(member)}
            onViewPhoto={openPhoto}
          >
            <DashboardMemberNameLabel baseName={baseName} />
          </DashboardPhotoName>
          {showInLawAsterisk && (
            <DashboardInLawAsterisk isLargeView={isLargeView} />
          )}
          {isDashboardDeceasedMember(member) && (
            <DashboardDeceasedAngel isLargeView={isLargeView} />
          )}
          {multipleBirthSup && (
            <sup className={`dashboard-multiple-birth-sup${isLargeView ? " large-view" : ""}`}>{multipleBirthSup}</sup>
          )}
          </DashboardNameMarkerGroup>
        </div>

        {/* Column 2: Birth month, short ("Jan"). As wide as the widest month
            on screen, so the line column starts in one place. */}
        <div className="birth-by-day-member-month" style={{ minWidth: "var(--birth-by-day-month-col, 0px)" }}>
          <span className="birth-by-day-month-text">{getBirthMonthShortName(member)}</span>
        </div>

        {/* Column 3: Head of Line Name and Color. Rendered on both layouts — the
            compact one scrolls sideways to reach it rather than dropping it.
            As wide as its own text, so the list (and its scrollbar) ends 3
            spaces past the longest line on screen. Compact drops the head's
            name and prints the line colour alone; the footer legend ties each
            colour to its head. */}
        <div className={`ages-milestone-member-head ${isLargeView ? 'large-view' : ''}`}>
          {isCompactLayout ? (
            <div className="dashboard-detail-line-col--compact">
              <DashboardLineColorName colorName={colorName} hexCode={hexCode} />
            </div>
          ) : (
          <div className={isLargeView ? 'large-view-text' : ''}>
            <DashboardPhotoHeadLine
              name={headOfLineName || "Start/Top of Line"}
              hexCode={hexCode}
              colorName={colorName ? `${colorName} Line` : ""}
              imageUrl={getDashboardMemberPhotoUrl(member, "head_image")}
              onViewPhoto={openPhoto}
              member={member}
              headsMap={headsMap}
            />
          </div>
          )}
        </div>
      </div>
    );
  };

  // The name column is as wide as the longest name on screen, measured off the
  // names as they actually render (marker group shrink-wraps the name, angel
  // and twin tag) — an off-screen copy came out wider than the real text and
  // left a gap far past 3 spaces. The line column then sits 3ch after it (see
  // .birth-by-day-page in App.css). Cleared first so the cells shrink back to
  // their own text before they are read.
  const measureNameColumn = () => {
    const list = listRef.current;
    if (!list) return;
    list.style.setProperty("--birth-by-day-name-col", "0px");
    let maxNameWidth = 0;
    list.querySelectorAll(".ages-milestone-member-name > .dashboard-name-marker-group").forEach((group) => {
      maxNameWidth = Math.max(maxNameWidth, group.getBoundingClientRect().width);
    });
    list.style.setProperty("--birth-by-day-name-col", `${Math.ceil(maxNameWidth)}px`);

    list.style.setProperty("--birth-by-day-month-col", "0px");
    let maxMonthWidth = 0;
    list.querySelectorAll(".birth-by-day-month-text").forEach((month) => {
      maxMonthWidth = Math.max(maxMonthWidth, month.getBoundingClientRect().width);
    });
    list.style.setProperty("--birth-by-day-month-col", `${Math.ceil(maxMonthWidth)}px`);
  };

  const getMemberLineColor = (member) =>
    (member?.family_line_color || member?.color_name || "").toLowerCase();

  // The opening view is every row birth-by-day returns for the day, in-laws
  // included, so each day's total matches the API's total_count. A member tab
  // narrows it, then the search box narrows it again; a day nobody in it
  // matches drops out.
  const filteredDays = useMemo(
    () =>
      groupedDays
        .map((group) => ({
          ...group,
          details: filterDashboardMembersBySearch(
            filterDashboardMembersByMemberFilter(group.details, memberFilter),
            searchQuery,
            formatPersonName
          ),
        }))
        .filter((group) => group.details.length > 0),
    [groupedDays, memberFilter, searchQuery]
  );

  // A member tab clears the sorts on the left of the bar; with none lit the
  // list reads in the tab's own order (deceased by year of death, living
  // descendants oldest first, living in-laws A–Z), else this report's A–Z.
  const toggleMemberFilter = (filter) => {
    setMemberFilter(applyDashboardMemberFilterToggle(memberFilter, filter, setSortBy));
  };
  const unsortedSort = getDashboardBaseSortForMemberFilter(memberFilter, DEFAULT_SORT);
  const effectiveSort = sortBy === "none" ? unsortedSort : sortBy;

  const isSearching = searchQuery.trim().length > 0;

  const filteredTotal = useMemo(
    () => filteredDays.reduce((sum, group) => sum + group.details.length, 0),
    [filteredDays]
  );

  // Days always run 1 through 31; the sort orders the people inside each day.
  const sortedDays = useMemo(() => {
    const byDob = (direction) => (a, b) => {
      const dobA = getMemberDobTimestamp(a);
      const dobB = getMemberDobTimestamp(b);
      if (dobA === -1 && dobB === -1) return 0;
      if (dobA === -1) return 1;
      if (dobB === -1) return -1;
      return direction * (dobA - dobB);
    };
    const byName = (a, b) =>
      formatPersonName(a).toLowerCase().localeCompare(formatPersonName(b).toLowerCase());

    return filteredDays.map((group) => {
      const details = [...group.details];
      if (effectiveSort === "death-asc") {
        return { ...group, details: sortDashboardMembersByDeathDate(details) };
      }
      if (effectiveSort === "alpha-asc") details.sort(byName);
      else if (effectiveSort === "alpha-desc") details.sort((a, b) => byName(b, a));
      else if (effectiveSort === "oldest") details.sort(byDob(1));
      else if (effectiveSort === "youngest") details.sort(byDob(-1));
      // Month: Jan → Dec within the day, A–Z inside each month; no date last.
      else if (effectiveSort === "month") {
        details.sort((a, b) => {
          const monthA = getBirthMonthIndex(a);
          const monthB = getBirthMonthIndex(b);
          if (monthA !== monthB) {
            if (monthA === -1) return 1;
            if (monthB === -1) return -1;
            return monthA - monthB;
          }
          return byName(a, b);
        });
      }
      else if (effectiveSort === "color") {
        details.sort((a, b) => {
          const colorA = getMemberLineColor(a);
          const colorB = getMemberLineColor(b);
          return colorA !== colorB ? colorA.localeCompare(colorB) : byName(a, b);
        });
      }
      return { ...group, details };
    });
  }, [filteredDays, effectiveSort]);

  useLayoutEffect(() => {
    if (loading) return undefined;
    measureNameColumn();
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) measureNameColumn();
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, sortedDays, isCompactLayout, isLargeView]);

  /** One day: its badge, the rule across, the day's total and the rows. */
  const renderDayGroup = (dayGroup) => {
    const { day, details: members } = dayGroup;

    return (
      <div key={day} className="ages-milestone-group">
        <div className="ages-milestone-group-header">
          <span className="ages-milestone-badge">{day}</span>
          <div className="ages-milestone-divider"></div>
          <span className="ages-milestone-count-badge">{members.length}</span>
        </div>

        <div className="ages-milestone-members-container">
          <span className="ages-milestone-spacer-badge" style={{ visibility: 'hidden' }}>
            {day}
          </span>
          <div className="ages-milestone-members-list">
            {members.map((member, index) => (
              <div key={member?.id || index}>
                {renderMemberRow(member)}
              </div>
            ))}
          </div>
          <span className="ages-milestone-spacer-badge" style={{ visibility: 'hidden' }}>
            {members.length}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={`dashboard-container ages-milestone-container current-year-milestone-ages-page birth-by-day-page dashboard-detail-page ${isLargeView ? 'large-view-mode' : ''}`}>
      <div className="dash-box ages-milestone-box dashboard-detail-box">
        <div className="ages-milestone-header">
          <div className="ages-milestone-header-left">
            <DashboardBackButton />
            <h3 className="ages-milestone-title">Birth Day (1-31, Calendar)</h3>
            <div className="ages-milestone-count">
              {filteredTotal}
            </div>
            {!loading && allMembers.length > 0 && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                endAtControl='[data-sort="youngest"]'
                centreOverDivider={false}
              />
            )}
            {!loading && allMembers.length > 0 && (
              <DashboardDetailSortFilterBar
                className="ages-milestone-sort-toggle"
                buttonClassName="ages-milestone-sort-btn"
                sortBy={sortBy}
                onSortChange={setSortBy}
                defaultSort={DEFAULT_SORT}
                // With no sort picked — after a member tab is tapped, or a lit
                // sort tapped off — the list still reads in the tab's own order
                // (A–Z on the opening view), so that button stays grayed out as
                // the one already applied.
                defaultSortByMemberFilter={{ [memberFilter]: unsortedSort }}
                memberFilter={memberFilter}
                onMemberFilterToggle={toggleMemberFilter}
                // Month sorts the month column, as Line Color sorts the line one.
                leadingSortButtons={
                  <button
                    type="button"
                    onClick={() => setSortBy(sortBy === "month" ? "none" : "month")}
                    className={`ages-milestone-sort-btn${sortBy === "month" ? " active" : ""}`}
                    data-sort="month"
                    title="Sort by Birth Month (Jan-Dec)"
                  >
                    Month
                  </button>
                }
                excludedMemberFilters={[
                  DASHBOARD_MEMBER_FILTERS.START_TOP,
                  DASHBOARD_MEMBER_FILTERS.DESCENDANTS,
                  DASHBOARD_MEMBER_FILTERS.IN_RELATIONSHIP,
                ]}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="ages-milestone-empty-state">
            <p>Loading birth day...</p>
          </div>
        ) : allMembers.length === 0 ? (
          <div className="ages-milestone-empty-state">
            <p>No data available.</p>
          </div>
        ) : filteredTotal === 0 ? (
          <div className="ages-milestone-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No data available.</p>
            )}
          </div>
        ) : (
          <>
            {/* Compact layout scrolls sideways once the head-of-line column pushes
                rows past the card; the column header rides inside the scroller
                so "Grp Totals" stays over its badge. */}
            <div className="ages-milestone-hscroll">
            <div className="ages-milestone-hscroll-track">
            <div className="ages-milestone-list" ref={listRef}>
              <div className="ages-milestone-column-header ages-milestone-column-header-inline">
                <span className="ages-milestone-column-label">Days</span>
                <span className="ages-milestone-column-header-spacer" aria-hidden="true" />
                <span className="ages-milestone-column-label ages-milestone-column-label-totals">
                  Grp Totals
                </span>
              </div>
              {sortedDays.map((group) => renderDayGroup(group))}
            </div>
            </div>
            </div>
            <div className="ages-milestone-page in-law-footer">
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(
                  sortedDays.map((group) => group.details)
                )}
              />
              {isCompactLayout && legendLines.length > 0 && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default BirthByDay;
